import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Loader2, CheckCircle, ArrowLeft, AlertTriangle, Truck, FileText, ShieldCheck, MapPinned, Phone } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPRESS_DELIVERY_FEE = 40.00;
// Daily cutoff at 16h00 local time. If the order is placed before 16h, "today"
// counts as day 0. If past 16h, "today" doesn't count anymore: day 0 = tomorrow.
// Free delivery requires arrival ≥ effective day 0 + 3 calendar days.
const DAILY_CUTOFF_HOUR = 16;
const MIN_DAYS_FOR_FREE_DELIVERY = 3;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { items, totalPrice, clearCart } = useCart();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_postal_code: '',
    notes: '',
    // Billing
    billing_first_name: '',
    billing_last_name: '',
    billing_address: '',
    billing_postal_code: '',
    billing_city: '',
  });
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(true);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);

  // Pre-fill billing identity from logged-in user
  React.useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        billing_first_name: prev.billing_first_name || user.first_name || '',
        billing_last_name: prev.billing_last_name || user.last_name || '',
      }));
    }
  }, [user]);

  // Calculate if express delivery is needed.
  // Rule: orders before 16h00 → today is day 0; orders at 16h00+ → day 0 = tomorrow.
  // Free delivery iff arrival_date ≥ day 0 + 3 calendar days.
  const deliveryInfo = useMemo(() => {
    if (!arrivalDate) {
      return { isExpress: false, fee: 0, freeDeliveryFrom: null };
    }

    const now = new Date();
    // Effective "day 0" = start of today if before 16h, else start of tomorrow
    const dayZero = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (now.getHours() >= DAILY_CUTOFF_HOUR) {
      dayZero.setDate(dayZero.getDate() + 1);
    }
    // Free delivery requires arrival ≥ dayZero + MIN_DAYS_FOR_FREE_DELIVERY (midnight)
    const freeDeliveryFrom = new Date(dayZero);
    freeDeliveryFrom.setDate(freeDeliveryFrom.getDate() + MIN_DAYS_FOR_FREE_DELIVERY);

    // Compare on calendar day basis (arrival is a date at 00h locally)
    const arrivalDay = new Date(arrivalDate.getFullYear(), arrivalDate.getMonth(), arrivalDate.getDate());
    const isExpress = arrivalDay < freeDeliveryFrom;

    return {
      isExpress,
      fee: isExpress ? EXPRESS_DELIVERY_FEE : 0,
      freeDeliveryFrom,
    };
  }, [arrivalDate]);

  const BOOKING_FEE = 1.00; // synced with backend BOOKING_FEE
  const finalTotal = totalPrice + deliveryInfo.fee + BOOKING_FEE;

  React.useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/checkout');
    }
  }, [user, authLoading, navigate]);

  React.useEffect(() => {
    if (!authLoading && user && items.length === 0 && !orderComplete) {
      navigate('/products');
    }
  }, [items, authLoading, user, navigate, orderComplete]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!arrivalDate || !departureDate) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner vos dates de séjour",
        variant: "destructive",
      });
      return;
    }

    // If billing differs, require all billing fields
    if (!billingSameAsDelivery) {
      const required = ['billing_first_name', 'billing_last_name', 'billing_address', 'billing_postal_code', 'billing_city'];
      const missing = required.filter((k) => !formData[k]?.trim());
      if (missing.length > 0) {
        toast({
          title: "Adresse de facturation incomplète",
          description: "Merci de remplir toutes les informations de facturation.",
          variant: "destructive",
        });
        return;
      }
    }

    if (!acceptedTerms) {
      toast({
        title: "Acceptation requise",
        description: "Vous devez accepter les CGV et la renonciation au droit de rétractation pour valider votre commande.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
        })),
        delivery_address: formData.delivery_address,
        delivery_city: formData.delivery_city,
        delivery_postal_code: formData.delivery_postal_code,
        delivery_country: 'France',
        billing_same_as_delivery: billingSameAsDelivery,
        billing_first_name: billingSameAsDelivery ? null : formData.billing_first_name,
        billing_last_name: billingSameAsDelivery ? null : formData.billing_last_name,
        billing_address: billingSameAsDelivery ? null : formData.billing_address,
        billing_postal_code: billingSameAsDelivery ? null : formData.billing_postal_code,
        billing_city: billingSameAsDelivery ? null : formData.billing_city,
        billing_country: 'France',
        arrival_date: format(arrivalDate, 'yyyy-MM-dd'),
        departure_date: format(departureDate, 'yyyy-MM-dd'),
        notes: formData.notes || null,
        express_delivery: deliveryInfo.isExpress,
        delivery_fee: deliveryInfo.fee,
        terms_accepted: acceptedTerms,
        // Where Mollie should send the customer back (current environment URL)
        frontend_origin: window.location.origin,
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const checkoutUrl = response.data.checkout_url;
      if (!checkoutUrl) {
        throw new Error("Aucune URL de paiement reçue");
      }

      // Persist cart-empty intent so /checkout/return can clear it after redirect
      localStorage.setItem('pending_order_id', response.data.id);

      toast({
        title: "Redirection vers le paiement sécurisé",
        description: "Vous allez être redirigé vers Mollie.",
      });

      // Hard redirect to Mollie hosted checkout (CB / Apple Pay / Visa / etc.)
      window.location.href = checkoutUrl;
      return;
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-medium text-[#2D3E50] mb-2">
            Commande confirmée !
          </h1>
          <p className="text-[#64748B] mb-2">
            Merci pour votre commande. Votre linge sera livré dans votre logement avant votre arrivée.
          </p>
          <p className="text-sm text-[#64748B] mb-8">
            Commande #{orderId?.slice(0, 8).toUpperCase()}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard"
            >
              Voir mes commandes
            </Button>
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => navigate('/')}
            >
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 bg-[#FAFAFA]" data-testid="checkout-page">
      {/* Header */}
      <section className="bg-[#2D3E50] py-8 md:py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>
          <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl tracking-tight font-medium text-white">
            Finaliser ma commande
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
              {/* Delivery Address - where the linen will be dropped off */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50]">
                      Adresse de livraison
                    </h2>
                    <p className="text-xs text-[#64748B]">
                      Où nous déposons le linge — votre logement de location, gîte ou Airbnb.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="delivery_address">Adresse</Label>
                    <Input
                      id="delivery_address"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      placeholder="123 rue de la Plage"
                      className="mt-1.5 h-12 rounded-lg border-gray-200"
                      required
                      data-testid="checkout-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="delivery_city">Ville</Label>
                      <Input
                        id="delivery_city"
                        name="delivery_city"
                        value={formData.delivery_city}
                        onChange={handleChange}
                        placeholder="Saint-Malo"
                        className="mt-1.5 h-12 rounded-lg border-gray-200"
                        required
                        data-testid="checkout-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="delivery_postal_code">Code postal</Label>
                      <Input
                        id="delivery_postal_code"
                        name="delivery_postal_code"
                        value={formData.delivery_postal_code}
                        onChange={handleChange}
                        placeholder="35400"
                        className="mt-1.5 h-12 rounded-lg border-gray-200"
                        required
                        data-testid="checkout-postal"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address - identity & address that appears on the invoice */}
              <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="billing-block">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-[#5493CC] flex-shrink-0 mt-0.5" />
                    <div>
                      <h2 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50]">
                        Adresse de facturation
                      </h2>
                      <p className="text-xs text-[#64748B]">
                        Identité et adresse qui figureront sur votre facture PDF.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-[#F2F8FD] border border-[#5493CC]/20">
                  <Checkbox
                    id="billing-same"
                    checked={billingSameAsDelivery}
                    onCheckedChange={(v) => setBillingSameAsDelivery(v === true)}
                    className="mt-0.5 border-[#5493CC] data-[state=checked]:bg-[#5493CC] data-[state=checked]:border-[#5493CC]"
                    data-testid="billing-same-toggle"
                  />
                  <Label htmlFor="billing-same" className="text-sm text-[#2D3E50] cursor-pointer leading-snug font-normal">
                    <span className="font-medium">Identique à l'adresse de livraison</span>
                    <br />
                    <span className="text-xs text-[#64748B]">
                      Cochez si vous voulez la facture à votre nom et à l'adresse du logement de location.
                    </span>
                  </Label>
                </div>

                {!billingSameAsDelivery && (
                  <div className="space-y-4 mt-5 pt-5 border-t border-gray-100" data-testid="billing-fields">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="billing_first_name">Prénom</Label>
                        <Input
                          id="billing_first_name"
                          name="billing_first_name"
                          value={formData.billing_first_name}
                          onChange={handleChange}
                          placeholder="Sophie"
                          className="mt-1.5 h-12 rounded-lg border-gray-200"
                          required={!billingSameAsDelivery}
                          data-testid="billing-first-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_last_name">Nom</Label>
                        <Input
                          id="billing_last_name"
                          name="billing_last_name"
                          value={formData.billing_last_name}
                          onChange={handleChange}
                          placeholder="Durand"
                          className="mt-1.5 h-12 rounded-lg border-gray-200"
                          required={!billingSameAsDelivery}
                          data-testid="billing-last-name"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="billing_address">Adresse</Label>
                      <Input
                        id="billing_address"
                        name="billing_address"
                        value={formData.billing_address}
                        onChange={handleChange}
                        placeholder="22 avenue de Paris"
                        className="mt-1.5 h-12 rounded-lg border-gray-200"
                        required={!billingSameAsDelivery}
                        data-testid="billing-address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="billing_city">Ville</Label>
                        <Input
                          id="billing_city"
                          name="billing_city"
                          value={formData.billing_city}
                          onChange={handleChange}
                          placeholder="Paris"
                          className="mt-1.5 h-12 rounded-lg border-gray-200"
                          required={!billingSameAsDelivery}
                          data-testid="billing-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing_postal_code">Code postal</Label>
                        <Input
                          id="billing_postal_code"
                          name="billing_postal_code"
                          value={formData.billing_postal_code}
                          onChange={handleChange}
                          placeholder="75008"
                          className="mt-1.5 h-12 rounded-lg border-gray-200"
                          required={!billingSameAsDelivery}
                          data-testid="billing-postal"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50] mb-6 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#E8945A]" />
                  Dates de séjour
                </h2>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date d'arrivée</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal mt-1.5 rounded-lg border-gray-200"
                          data-testid="checkout-arrival"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-[#64748B]" />
                          {arrivalDate ? format(arrivalDate, 'dd MMM yyyy', { locale: fr }) : 'Sélectionner'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={arrivalDate}
                          onSelect={setArrivalDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label>Date de départ</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal mt-1.5 rounded-lg border-gray-200"
                          data-testid="checkout-departure"
                        >
                          <Calendar className="mr-2 h-4 w-4 text-[#64748B]" />
                          {departureDate ? format(departureDate, 'dd MMM yyyy', { locale: fr }) : 'Sélectionner'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={departureDate}
                          onSelect={setDepartureDate}
                          disabled={(date) => date < (arrivalDate || new Date())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Express delivery warning */}
                {deliveryInfo.isExpress && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Livraison express
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        Pour bénéficier de la livraison gratuite, votre arrivée doit être prévue
                        à partir du{' '}
                        <span className="font-semibold">
                          {deliveryInfo.freeDeliveryFrom
                            ? format(deliveryInfo.freeDeliveryFrom, 'EEEE dd MMMM', { locale: fr })
                            : ''}
                        </span>{' '}
                        (3 jours après la commande, coupure à 16h00).
                        Sinon, des frais de livraison express de <span className="font-semibold">40,00 €</span> s'appliquent.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50] mb-4">
                  Instructions spéciales (optionnel)
                </h2>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Ex: Code d'accès, heure d'arrivée prévue..."
                  className="rounded-lg border-gray-200 min-h-[100px]"
                  data-testid="checkout-notes"
                />
              </div>

              {/* Acceptation CGV & renonciation au droit de rétractation */}
              <div className="bg-white rounded-xl border-2 border-[#E8945A]/30 p-6" data-testid="checkout-terms-block">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                    className="mt-1 border-[#2D3E50] data-[state=checked]:bg-[#E8945A] data-[state=checked]:border-[#E8945A]"
                    data-testid="checkout-accept-terms"
                  />
                  <Label
                    htmlFor="accept-terms"
                    className="text-sm text-[#2D3E50] leading-relaxed cursor-pointer font-normal"
                  >
                    En cochant cette case, je <strong>demande expressément l'exécution immédiate</strong> du
                    service de location et de livraison de linge, et je <strong>renonce expressément à mon droit
                    de rétractation</strong> de 14 jours conformément à l'article L.221-28 du Code de la consommation.
                    Je reconnais qu'<strong>aucun remboursement</strong> ne pourra être exigé une fois la commande validée
                    et payée. J'accepte les{' '}
                    <a
                      href="/cgv"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#E8945A] underline font-medium"
                      data-testid="checkout-cgv-link"
                    >
                      Conditions Générales de Vente
                    </a>
                    {' '}ainsi que la <strong>caution de 150 € maximum</strong> via empreinte bancaire pour
                    couvrir vol ou dégradation du linge.
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-[#E8945A] hover:bg-[#D4844A] rounded-lg text-base disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !acceptedTerms}
                data-testid="checkout-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Redirection...
                  </>
                ) : (
                  `Payer ${finalTotal.toFixed(2)} € en ligne`
                )}
              </Button>
              <p className="text-xs text-center text-[#64748B] -mt-2">
                Paiement sécurisé via Mollie · CB · Apple Pay · Visa · Mastercard · Bancontact
              </p>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-100 p-6 sticky top-24">
              <h2 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50] mb-6">
                Récapitulatif
              </h2>

              <div className="space-y-3 mb-6">
                {items.map(({ product, quantity }) => (
                  <div key={product.id} className="flex justify-between items-center py-2 border-b border-gray-50">
                    <div className="flex-1 min-w-0 mr-4">
                      <h4 className="text-sm font-medium text-[#2D3E50]">{product.name}</h4>
                      <p className="text-xs text-[#64748B]">Qté: {quantity}</p>
                    </div>
                    <p className="text-sm font-semibold text-[#2D3E50]">
                      {(product.price * quantity).toFixed(2)} €
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748B]">Sous-total</span>
                  <span className="text-[#2D3E50]">{totalPrice.toFixed(2)} €</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-[#64748B]" />
                    <span className="text-[#64748B]">Livraison</span>
                  </div>
                  {deliveryInfo.isExpress ? (
                    <span className="text-amber-600 font-medium">40,00 € (express)</span>
                  ) : arrivalDate ? (
                    <span className="text-green-600 font-medium">Gratuite</span>
                  ) : (
                    <span className="text-[#64748B]">-</span>
                  )}
                </div>

                {!arrivalDate && (
                  <p className="text-xs text-[#64748B] italic">
                    Sélectionnez une date d'arrivée pour connaître les frais de livraison
                  </p>
                )}

                {arrivalDate && !deliveryInfo.isExpress && (
                  <p className="text-xs text-green-600">
                    Livraison gratuite (commande 3 jours pleins avant l'arrivée, coupure à 16h00)
                  </p>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-[#64748B]">Frais de dossier</span>
                  <span className="text-[#2D3E50]">{BOOKING_FEE.toFixed(2)} €</span>
                </div>

                <div className="flex justify-between pt-3 border-t border-gray-100">
                  <span className="font-medium text-[#2D3E50]">Total</span>
                  <span className="text-xl font-semibold text-[#2D3E50]" data-testid="checkout-total">
                    {finalTotal.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
